import { useEffect, useState } from "react";
import { getAnalyticsConsentState } from "../../lib/consent";
import {
  AUTOMATIC_TRANSLATION_NOTICE,
  DEFAULT_LANGUAGE,
  LANGUAGE_OPTIONS,
  ensureGoogleTranslateScript,
  getPreferredLanguage,
  getLanguageLabel,
  hasHandledLanguagePrompt,
  markLanguagePromptHandled,
  readGoogleTranslateCookie,
  setGoogleTranslateCookie,
} from "../../lib/language";

const MODAL_COPY = {
  pl: {
    title: "Wykryto inny jezyk niz oryginalny",
    description: "Wybierz swoj jezyk. Language. Sprache.",
    continueLabel: "Kontynuuj po polsku",
  },
  en: {
    title: "We detected a language different from the original",
    description: "Choose your language. Language. Sprache. Jezyk.",
    continueLabel: "Continue in Polish",
  },
  de: {
    title: "Wir haben eine andere Sprache als das Original erkannt",
    description: "Wahlen Sie Ihre Sprache. Language. Sprache. Jezyk.",
    continueLabel: "Weiter auf Polnisch",
  },
};

function LanguageSwitcher() {
  const [selectedLanguage, setSelectedLanguage] = useState(DEFAULT_LANGUAGE);
  const [preferredLanguage, setPreferredLanguage] = useState(DEFAULT_LANGUAGE);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") {
      return undefined;
    }

    const detectedLanguage = getPreferredLanguage();
    const cookieLanguage = readGoogleTranslateCookie();
    const hasConsent = Boolean(getAnalyticsConsentState());

    setPreferredLanguage(detectedLanguage);
    setSelectedLanguage(cookieLanguage);

    ensureGoogleTranslateScript();

    if (
      hasConsent &&
      detectedLanguage !== DEFAULT_LANGUAGE &&
      !hasHandledLanguagePrompt()
    ) {
      setIsVisible(true);
    }

  }, []);

  const applyLanguage = (nextLanguage) => {
    setSelectedLanguage(nextLanguage);
    setGoogleTranslateCookie(nextLanguage);
    markLanguagePromptHandled();
    setIsVisible(false);
    window.location.reload();
  };

  const handleLanguageChange = (event) => {
    applyLanguage(event.target.value);
  };

  const handleContinueInPolish = () => {
    applyLanguage(DEFAULT_LANGUAGE);
  };

  if (!isVisible) {
    return <div id="google_translate_element" className="hidden" />;
  }

  const copy =
    MODAL_COPY[preferredLanguage] ||
    (preferredLanguage === DEFAULT_LANGUAGE ? MODAL_COPY.pl : MODAL_COPY.en);

  return (
    <>
      <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm">
        <div className="w-full max-w-md rounded-[28px] border border-white/10 bg-[#111111] p-6 text-white shadow-2xl">
          <p className="text-xs uppercase tracking-[0.3em] text-white/50">
            {preferredLanguage === DEFAULT_LANGUAGE ? "Jezyk" : "Language"}
          </p>
          <h2 className="mt-3 text-2xl font-semibold leading-tight">
            {copy.title}
          </h2>
          <p className="mt-3 text-sm leading-6 text-white/70">
            {copy.description}
          </p>
          <p className="mt-3 text-xs leading-5 text-white/50">
            {AUTOMATIC_TRANSLATION_NOTICE}
          </p>

          <div className="mt-6 grid grid-cols-2 gap-3">
            {LANGUAGE_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => applyLanguage(option.value)}
                className={`rounded-2xl border px-4 py-3 text-left text-sm transition ${
                  selectedLanguage === option.value
                    ? "border-[#d4af37] bg-[#d4af37]/10 text-white"
                    : "border-white/10 bg-white/5 text-white/80 hover:border-white/20 hover:bg-white/10"
                }`}
              >
                {getLanguageLabel(option, preferredLanguage)}
              </button>
            ))}
          </div>

          <div className="mt-5 flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={handleContinueInPolish}
              className="text-sm text-white/60 transition hover:text-white"
            >
              {copy.continueLabel}
            </button>
            <select
              aria-label="Language switcher"
              value={selectedLanguage}
              onChange={handleLanguageChange}
              className="rounded-full border border-white/10 bg-white/5 px-3 py-2 text-sm text-white outline-none"
            >
              {LANGUAGE_OPTIONS.map((option) => (
                <option
                  key={option.value}
                  value={option.value}
                  className="text-black"
                >
                  {getLanguageLabel(option, preferredLanguage)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
      <div id="google_translate_element" className="hidden" />
    </>
  );
}

export default LanguageSwitcher;
